/**
 * ModelOperations - 模型操作代理
 *
 * 封装对已注册模型的操作：拾取、颜色调节、标量过滤、选中、中心点显示等。
 * 这些方法本质上是查表 + 转发到底层组件，从 PCManager 中下沉到此处。
 */

import * as THREE from 'three'
import {
  type GSplatClipState,
  isGSplatCenterVisible,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import type { ColorSpaceConfig } from '@/plugins/gsplat/core/ColorSpaceConstants'
import { ColorSpaceManager } from '@/plugins/gsplat/core/ColorSpaceManager'
import type { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import { GSplatScalarMode } from '@/plugins/gsplat/lod/GSplatWorkBuffer'
import type {
  CentersDisplayMode,
  CentersStyle,
} from '@/plugins/gsplat/materials/CentersPointsMaterial'
import type { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import {
  CentersPickHandler,
  type CentersPickOptions,
  type CentersPickResult,
} from '@/plugins/gsplat/spatial/CentersPickHandler'
import {
  GSplatPickManager,
  type PickOptions,
  type PickResult,
} from '@/plugins/gsplat/spatial/GSplatPickManager'
import { getLoggerManager } from '@/shared/logging'
import type {
  BoundingBox,
  GSplatShaderEffectLike,
  GSplatWorkBufferModifier,
  GSplatWorkBufferUpdateMode,
  ScalarFilterOptions,
  ScalarHistogramOptions,
  ScalarHistogramResult,
} from '@/shared/types/gsplat'
import { getPickRayDistance, isPickPointAtOrBeyondMinDistance } from '@/shared/utils/pickDepth'
import { convertBox3ToBoundingBox } from './ModelLoader'
import type { ModelRegistry } from './ModelRegistry'
import type { LODGroupEntry } from './types'

const log = getLoggerManager().getLogger('gsplat')

const scalarFilterDebugGlobal = globalThis as typeof globalThis & {
  __POTREE_GS_SCALAR_DEBUG__?: boolean
}

function isScalarFilterDebugEnabled(): boolean {
  return scalarFilterDebugGlobal.__POTREE_GS_SCALAR_DEBUG__ === true
}

/** 标量类型字符串到枚举的映射 */
const SCALAR_TYPE_MAP: Record<string, GSplatScalarMode> = {
  volume: GSplatScalarMode.Volume,
  'surface-area': GSplatScalarMode.SurfaceArea,
  value: GSplatScalarMode.Value,
}

/** ModelOperations 依赖的上下文 */
export interface ModelOperationsContext {
  /** 挂载模型的场景 */
  scene: THREE.Scene
  /** 当前 WebGL 渲染器 */
  renderer: THREE.WebGLRenderer
  /** 模型注册表 */
  registry: ModelRegistry
  /** 取当前 Director，尚未就绪返回 null */
  getDirector: () => GSplatDirector | null
  /** 取上一次使用的相机，尚未使用返回 null */
  getLastCamera: () => THREE.Camera | null
  /** 取所有活跃的 GSplatManager */
  getAllManagers: () => GSplatManager[]
  /** 取当前 unified LOD polygon 裁切状态 */
  getClipState: () => GSplatClipState
}

/** 模型操作代理 */
export class ModelOperations {
  /** 颜色空间管理器（懒加载） */
  private colorSpaceManager: ColorSpaceManager | null = null

  /** 复用临时变量 */
  private _tempBox = new THREE.Box3()

  constructor(private ctx: ModelOperationsContext) {}

  // ==================== 包围盒 ====================

  /**
   * 按 nodeId 获取世界坐标系下的包围盒。
   *
   * @param nodeId - 模型唯一标识
   * @returns 包围盒描述；未找到返回 undefined
   */
  getBoundingBoxByNodeId(nodeId: string): BoundingBox | undefined {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (lodEntry) {
      this._tempBox.copy(lodEntry.boundingBox)
      lodEntry.rootNode.updateMatrixWorld(true)
      this._tempBox.applyMatrix4(lodEntry.rootNode.matrixWorld)
      return convertBox3ToBoundingBox(this._tempBox)
    }

    const singleEntry = this.ctx.registry.getSingleByNodeId(nodeId)
    if (singleEntry) {
      this._tempBox.copy(singleEntry.boundingBox)
      singleEntry.mesh.updateMatrixWorld(true)
      this._tempBox.applyMatrix4(singleEntry.mesh.matrixWorld)
      return convertBox3ToBoundingBox(this._tempBox)
    }

    return undefined
  }

  /**
   * 取当前所有已注册模型的联合包围盒。
   *
   * @returns 世界坐标系下的联合包围盒
   */
  getCombinedBoundingBox(): BoundingBox {
    const combinedBox = new THREE.Box3()

    for (const entry of this.ctx.registry.lodGroups.values()) {
      this._tempBox.copy(entry.boundingBox)
      entry.rootNode.updateMatrixWorld(true)
      this._tempBox.applyMatrix4(entry.rootNode.matrixWorld)
      combinedBox.union(this._tempBox)
    }

    for (const entry of this.ctx.registry.singleFiles.values()) {
      this._tempBox.copy(entry.boundingBox)
      entry.mesh.updateMatrixWorld(true)
      this._tempBox.applyMatrix4(entry.mesh.matrixWorld)
      combinedBox.union(this._tempBox)
    }

    return convertBox3ToBoundingBox(combinedBox)
  }

  /**
   * 切换 LOD 模型的包围盒辅助可视化。
   *
   * @param visible - true 为显示，false 为隐藏
   * @param nodeIds - 作用的模型 ID，省略时作用于全部 LOD 模型
   */
  setBoxHelperVisible(visible: boolean, nodeIds?: string[]): void {
    const targetNodeIds = nodeIds ?? this.ctx.registry.getAllNodeIds()

    for (const nodeId of targetNodeIds) {
      const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
      if (!lodEntry) continue

      if (visible) {
        if (!lodEntry.boxHelper) {
          this._tempBox.copy(lodEntry.boundingBox)
          lodEntry.rootNode.updateMatrixWorld(true)
          this._tempBox.applyMatrix4(lodEntry.rootNode.matrixWorld)
          lodEntry.boxHelper = new THREE.Box3Helper(this._tempBox, new THREE.Color('#0A65B9'))
          this.ctx.scene.add(lodEntry.boxHelper)
        }
        lodEntry.boxHelper.visible = true
      } else if (lodEntry.boxHelper) {
        lodEntry.boxHelper.visible = false
      }
    }
  }

  // ==================== 拾取 ====================

  /**
   * 按射线拾取 3DGS 模型。
   *
   * @param ray - 世界空间射线
   * @param camera - 当前相机，用于 GPU 拾取视图
   * @param forceCpuPickNodeIds - 需强制走 CPU 拾取的 nodeId 集合
   * @param options - 可选拾取参数
   * @returns 命中结果；未命中返回 null
   */
  pick(
    ray: THREE.Ray,
    camera: THREE.Camera,
    forceCpuPickNodeIds: Set<string>,
    options?: PickOptions
  ): PickResult | null {
    if (!this.ctx.getDirector()) {
      return this.pickSingleFiles(ray, options)
    }

    const managers = this.ctx.getAllManagers()
    if (managers.length === 0) {
      return this.pickSingleFiles(ray, options)
    }

    // 检查是否有模型需要强制使用 CPU 拾取
    let shouldForceCpu = forceCpuPickNodeIds.size > 0
    if (!shouldForceCpu) {
      for (const manager of managers) {
        if (manager.loadingCount > 0) {
          shouldForceCpu = true
          break
        }
      }
    }

    const finalOptions: PickOptions = {
      ...options,
      interactionType: shouldForceCpu ? 'move' : (options?.interactionType ?? 'auto'),
      clipState: this.ctx.getClipState(),
    }

    return (
      GSplatPickManager.pick(ray, camera, this.ctx.renderer, managers, finalOptions) ??
      this.pickSingleFiles(ray, finalOptions)
    )
  }

  /**
   * 按屏幕坐标拾取 3DGS 模型。
   *
   * @param screenX - 屏幕 x，CSS 像素
   * @param screenY - 屏幕 y，CSS 像素
   * @param camera - 当前相机
   * @param forceCpuPickNodeIds - 需强制走 CPU 拾取的 nodeId 集合
   * @param options - 可选拾取参数
   * @returns 命中结果；未命中返回 null
   */
  pickFromScreen(
    screenX: number,
    screenY: number,
    camera: THREE.Camera,
    forceCpuPickNodeIds: Set<string>,
    options?: PickOptions
  ): PickResult | null {
    const { width, height } = this.resolvePickScreenSize(options)
    const ndcX = (screenX / width) * 2 - 1
    const ndcY = -(screenY / height) * 2 + 1

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)

    return this.pick(raycaster.ray, camera, forceCpuPickNodeIds, {
      ...options,
      screenX,
      screenY,
    })
  }

  /** 释放 GPU 拾取器占用的显存资源。 */
  disposeGpuPicker(): void {
    GSplatPickManager.disposeGpuPicker(this.ctx.renderer)
  }

  /** 清空 GPU 拾取器缓存的 LOD 版本号，强制下次重采。 */
  clearGpuPickerLodVersions(): void {
    GSplatPickManager.clearAllGpuPickerLodVersions()
  }

  /**
   * 按屏幕点取 splat 中心点。
   *
   * @param screenX - 屏幕 x，CSS 像素
   * @param screenY - 屏幕 y，CSS 像素
   * @param camera - 当前相机
   * @param options - 可选中心点拾取参数
   * @returns 命中中心点数组，按距离近到远
   */
  pickCenter(
    screenX: number,
    screenY: number,
    camera: THREE.Camera,
    options?: CentersPickOptions
  ): CentersPickResult[] {
    const canvas = this.ctx.renderer.domElement
    const allSplatInfos = this.collectAllSplatInfos()
    if (allSplatInfos.length === 0) return []

    const { width: screenWidth, height: screenHeight } = this.resolvePickScreenSize(options, {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    })

    const clipState = this.ctx.getClipState()
    const accept =
      clipState.volumes.length > 0
        ? (result: CentersPickResult) => isGSplatCenterVisible(clipState, result.worldPosition)
        : undefined

    return CentersPickHandler.pickAtScreen(
      screenX,
      screenY,
      screenWidth,
      screenHeight,
      camera,
      allSplatInfos,
      options,
      accept
    )
  }

  private resolvePickScreenSize(
    options?: PickOptions | CentersPickOptions,
    fallback?: { width: number; height: number }
  ): { width: number; height: number } {
    if (
      Number.isFinite(options?.screenWidth) &&
      Number.isFinite(options?.screenHeight) &&
      (options?.screenWidth as number) > 0 &&
      (options?.screenHeight as number) > 0
    ) {
      return {
        width: options?.screenWidth as number,
        height: options?.screenHeight as number,
      }
    }

    if (fallback && fallback.width > 0 && fallback.height > 0) {
      return fallback
    }

    return this.ctx.renderer.getSize(new THREE.Vector2())
  }

  /**
   * 按屏幕矩形框选 splat 中心点。
   *
   * @param startX - 起点 x，CSS 像素
   * @param startY - 起点 y，CSS 像素
   * @param endX - 终点 x，CSS 像素
   * @param endY - 终点 y，CSS 像素
   * @param camera - 当前相机
   * @param maxResults - 返回上限，默认 10000
   * @returns 命中中心点数组
   */
  pickCentersInRect(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    camera: THREE.Camera,
    maxResults = 10000
  ): CentersPickResult[] {
    const canvas = this.ctx.renderer.domElement
    const allSplatInfos = this.collectAllSplatInfos()
    if (allSplatInfos.length === 0) return []

    const clipState = this.ctx.getClipState()
    const accept =
      clipState.volumes.length > 0
        ? (result: CentersPickResult) => isGSplatCenterVisible(clipState, result.worldPosition)
        : undefined

    return CentersPickHandler.pickInRect(
      startX,
      startY,
      endX,
      endY,
      canvas.clientWidth,
      canvas.clientHeight,
      camera,
      allSplatInfos,
      maxResults,
      accept
    )
  }

  private collectAllSplatInfos(): import('@/plugins/gsplat/lod/GSplatInfo').GSplatInfo[] {
    const allSplatInfos: import('@/plugins/gsplat/lod/GSplatInfo').GSplatInfo[] = []
    for (const entry of this.ctx.registry.lodGroups.values()) {
      const manager = this.getGSplatManager(entry)
      if (manager) {
        allSplatInfos.push(...manager.getActiveSplatInfos())
      }
    }
    return allSplatInfos
  }

  private pickSingleFiles(
    ray: THREE.Ray,
    options?: PickOptions
  ): (PickResult & { assetId?: string }) | null {
    if (this.ctx.registry.singleFiles.size === 0) {
      return null
    }

    const raycaster = new THREE.Raycaster(ray.origin, ray.direction)
    raycaster.near = options?.minDistance ?? 0
    const intersections: Array<THREE.Intersection & { index?: number }> = []

    for (const entry of this.ctx.registry.singleFiles.values()) {
      if (entry.mesh.visible === false) {
        continue
      }

      const beforeCount = intersections.length
      entry.mesh.raycast(raycaster, intersections)
      for (let i = beforeCount; i < intersections.length; i += 1) {
        intersections[i].object = entry.mesh
        ;(intersections[i] as THREE.Intersection & { assetId?: string }).assetId = entry.nodeId
      }
    }

    intersections.sort((a, b) => a.distance - b.distance)
    const hit = intersections.find((intersection) =>
      isPickPointAtOrBeyondMinDistance(ray, intersection.point, options?.minDistance)
    ) as (THREE.Intersection & { assetId?: string }) | undefined
    if (!hit) {
      return null
    }

    return {
      assetId: hit.assetId,
      location: hit.point.clone(),
      distance: getPickRayDistance(ray, hit.point),
      splatIndex: hit.index ?? -1,
      splatInfo: undefined as never,
    }
  }

  // ==================== 颜色调节 ====================

  /**
   * 设置模型的颜色调整参数。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 亮度/色温/对比度/饱和度/透明度，未提供的字段保持原值
   * @returns 设置成功返回 true
   */
  setColorAdjustment(
    nodeId: string,
    options: {
      brightness?: number
      temperature?: number
      contrast?: number
      saturation?: number
      opacity?: number
    }
  ): boolean {
    const material = this.getMaterialByNodeId(nodeId)
    if (!material) {
      log.warn(`[ModelOperations] setColorAdjustment: nodeId "${nodeId}" not found`)
      return false
    }
    material.setColorAdjustment(options)
    return true
  }

  /**
   * 获取模型当前的颜色调整参数。
   *
   * @param nodeId - 模型唯一标识
   * @returns 当前参数快照；未找到返回 undefined
   */
  getColorAdjustment(nodeId: string):
    | {
        brightness: number
        temperature: number
        contrast: number
        saturation: number
        opacity: number
      }
    | undefined {
    const material = this.getMaterialByNodeId(nodeId)
    return material?.getColorAdjustment()
  }

  /**
   * 重置模型颜色调整参数到默认值。
   *
   * @param nodeId - 模型唯一标识
   * @returns 重置成功返回 true
   */
  resetColorAdjustment(nodeId: string): boolean {
    const material = this.getMaterialByNodeId(nodeId)
    if (!material) {
      log.warn(`[ModelOperations] resetColorAdjustment: nodeId "${nodeId}" not found`)
      return false
    }
    material.resetColorAdjustment()
    return true
  }

  // ==================== 颜色空间 ====================

  /**
   * 合并更新全局颜色空间配置，并同步到现有单文件模型。
   *
   * @param config - 局部配置，仅覆盖提供的字段
   */
  setColorSpaceConfig(config: Partial<ColorSpaceConfig>): void {
    const manager = this.ensureColorSpaceManager()
    manager.setDefaults(config)

    for (const entry of this.ctx.registry.singleFiles.values()) {
      entry.mesh.setColorSpaceManager(manager)
    }
  }

  /**
   * 获取全局颜色空间配置。
   *
   * @returns 配置副本；尚未初始化时返回 undefined
   */
  getColorSpaceConfig(): ColorSpaceConfig | undefined {
    if (!this.colorSpaceManager) return undefined
    return { ...this.colorSpaceManager.getDefaults() }
  }

  // ==================== 标量过滤 ====================

  /**
   * 按标量属性设置过滤范围。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 过滤配置，含属性名与阈值区间
   * @returns 设置成功返回 true
   */
  setScalarFilter(nodeId: string, options: ScalarFilterOptions): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setScalarFilter: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    const manager = this.getGSplatManager(lodEntry)
    if (!manager) return false

    const mode = SCALAR_TYPE_MAP[options.type] ?? GSplatScalarMode.None
    if (isScalarFilterDebugEnabled()) {
      log.info(
        `[ModelOperations][ScalarFilter] setScalarFilter nodeId=${nodeId}, enabled=${options.enabled}, type=${options.type}, mode=${mode}, min=${options.min}, max=${options.max}`
      )
    }
    manager.setScalarFilter({ enabled: options.enabled, mode, min: options.min, max: options.max })
    return true
  }

  /**
   * 清除模型的标量过滤。
   *
   * @param nodeId - 模型唯一标识
   * @returns 清除成功返回 true
   */
  clearScalarFilter(nodeId: string): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] clearScalarFilter: nodeId "${nodeId}" not found`)
      return false
    }

    const manager = this.getGSplatManager(lodEntry)
    if (!manager) return false

    manager.clearScalarFilter()
    return true
  }

  /**
   * 计算模型指定标量属性的直方图。
   *
   * @param nodeId - 模型唯一标识
   * @param options - 直方图配置，含类型、分箱数等
   * @returns 直方图结果；模型不存在或属性为 None 时返回 null
   */
  async getScalarHistogram(
    nodeId: string,
    options: ScalarHistogramOptions
  ): Promise<ScalarHistogramResult | null> {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] getScalarHistogram: nodeId "${nodeId}" not found`)
      return null
    }

    const manager = this.getGSplatManager(lodEntry)
    if (!manager) return null

    const mode = SCALAR_TYPE_MAP[options.type] ?? GSplatScalarMode.None
    if (mode === GSplatScalarMode.None) return null

    const result = manager.getScalarHistogram({
      mode,
      binCount: options.binCount,
      scale: options.scale,
      percentileRange: options.percentileRange,
    })

    if (isScalarFilterDebugEnabled() && result) {
      log.info(
        `[ModelOperations][ScalarFilter] getScalarHistogram nodeId=${nodeId}, type=${options.type}, min=${result.min}, max=${result.max}, sampleCount=${result.sampleCount}`
      )
    }

    return result
  }

  // ==================== Shader Effect ====================

  /**
   * 设置最终渲染 shader effect。
   *
   * direct / SOGS 路径通常按独立材质隔离；unified LOD 会转发到共享 renderMesh，
   * 因此 effect 作用域是共享材质或整组 renderMesh，不是天然 per-node 隔离。
   */
  setShaderEffect(nodeId: string, effect: GSplatShaderEffectLike | null): boolean {
    const host = this.getShaderEffectHostByNodeId(nodeId)
    if (!host) {
      log.warn(`[ModelOperations] setShaderEffect: nodeId "${nodeId}" not found`)
      return false
    }
    return host.setShaderEffect(effect)
  }

  clearShaderEffect(nodeId: string): boolean {
    const host = this.getShaderEffectHostByNodeId(nodeId)
    if (!host) {
      log.warn(`[ModelOperations] clearShaderEffect: nodeId "${nodeId}" not found`)
      return false
    }
    return host.clearShaderEffect()
  }

  restartShaderEffect(nodeId: string): boolean {
    const host = this.getShaderEffectHostByNodeId(nodeId)
    if (!host) {
      log.warn(`[ModelOperations] restartShaderEffect: nodeId "${nodeId}" not found`)
      return false
    }
    return host.restartShaderEffect()
  }

  setShaderEffectUniform(nodeId: string, name: string, value: unknown): boolean {
    const host = this.getShaderEffectHostByNodeId(nodeId)
    if (!host) {
      log.warn(`[ModelOperations] setShaderEffectUniform: nodeId "${nodeId}" not found`)
      return false
    }

    const effect = host.getShaderEffect?.()
    if (effect?.setUniform) {
      return effect.setUniform(name, value)
    }

    const material = this.getMaterialByNodeId(nodeId)
    if (!material) {
      log.warn(`[ModelOperations] setShaderEffectUniform: nodeId "${nodeId}" not found`)
      return false
    }
    return material.setShaderEffectUniform(name, value)
  }

  // ==================== WorkBuffer 修改器 ====================

  setWorkBufferModifier(nodeId: string, modifier: GSplatWorkBufferModifier | null): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setWorkBufferModifier: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    return lodEntry.renderMesh.setWorkBufferModifier(modifier)
  }

  setWorkBufferUpdateMode(nodeId: string, mode: GSplatWorkBufferUpdateMode): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setWorkBufferUpdateMode: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    return lodEntry.renderMesh.setWorkBufferUpdateMode(mode)
  }

  // ==================== 选中 ====================

  /**
   * 标记指定 splat 为选中或取消选中。
   *
   * @param nodeId - 模型唯一标识
   * @param splatIndices - splat 索引数组
   * @param selected - true 选中，false 取消
   * @returns 操作成功返回 true
   */
  setSelected(nodeId: string, splatIndices: number[], selected: boolean): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setSelected: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    const manager = this.getGSplatManager(lodEntry)
    if (!manager) return false

    for (const splatInfo of manager.getActiveSplatInfos()) {
      splatInfo.setSelected(splatIndices, selected)
    }
    return true
  }

  /**
   * 清除模型的全部选中。
   *
   * @param nodeId - 模型唯一标识
   * @returns 操作成功返回 true
   */
  clearSelection(nodeId: string): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) return false

    const manager = this.getGSplatManager(lodEntry)
    if (!manager) return false

    for (const splatInfo of manager.getActiveSplatInfos()) {
      splatInfo.clearSelection()
    }
    return true
  }

  // ==================== 中心点显示 ====================

  /**
   * 设置 LOD 模型的中心点显示模式。
   *
   * @param nodeId - 模型唯一标识
   * @param mode - 显示模式枚举
   * @returns 设置成功返回 true
   */
  setCentersDisplayMode(nodeId: string, mode: CentersDisplayMode): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setCentersDisplayMode: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    lodEntry.centersDisplayMode = mode

    switch (mode) {
      case 'gaussian-only':
        lodEntry.renderMesh.visible = true
        lodEntry.centersPoints.visible = false
        break
      case 'centers-only':
        lodEntry.renderMesh.visible = false
        lodEntry.centersPoints.visible = true
        break
      case 'gaussian-with-centers':
        lodEntry.renderMesh.visible = true
        lodEntry.centersPoints.visible = true
        lodEntry.centersPoints.renderOrder = lodEntry.renderMesh.renderOrder + 1
        break
    }

    return true
  }

  /**
   * 获取 LOD 模型当前的中心点显示模式。
   *
   * @param nodeId - 模型唯一标识
   * @returns 当前模式；未找到返回 undefined
   */
  getCentersDisplayMode(nodeId: string): CentersDisplayMode | undefined {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    return lodEntry?.centersDisplayMode
  }

  /**
   * 合并更新 LOD 模型的中心点样式。
   *
   * @param nodeId - 模型唯一标识
   * @param style - 局部样式，仅覆盖提供的字段
   * @returns 设置成功返回 true
   */
  setCentersStyle(nodeId: string, style: Partial<CentersStyle>): boolean {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) {
      log.warn(`[ModelOperations] setCentersStyle: nodeId "${nodeId}" not found or not LOD`)
      return false
    }

    if (style.pointSize !== undefined) lodEntry.centersStyle.pointSize = style.pointSize
    if (style.selectedColor !== undefined)
      lodEntry.centersStyle.selectedColor.copy(style.selectedColor)
    if (style.selectedOpacity !== undefined)
      lodEntry.centersStyle.selectedOpacity = style.selectedOpacity
    if (style.unselectedColor !== undefined)
      lodEntry.centersStyle.unselectedColor.copy(style.unselectedColor)
    if (style.unselectedOpacity !== undefined)
      lodEntry.centersStyle.unselectedOpacity = style.unselectedOpacity

    lodEntry.centersPoints.material.setStyle(style)
    return true
  }

  /**
   * 获取 LOD 模型当前的中心点样式。
   *
   * @param nodeId - 模型唯一标识
   * @returns 样式副本；未找到返回 undefined
   */
  getCentersStyle(nodeId: string): CentersStyle | undefined {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (!lodEntry) return undefined

    return {
      pointSize: lodEntry.centersStyle.pointSize,
      selectedColor: lodEntry.centersStyle.selectedColor.clone(),
      selectedOpacity: lodEntry.centersStyle.selectedOpacity,
      unselectedColor: lodEntry.centersStyle.unselectedColor.clone(),
      unselectedOpacity: lodEntry.centersStyle.unselectedOpacity,
    }
  }

  // ==================== 生命周期 ====================

  /** 释放内部持有的颜色空间管理器等资源。 */
  dispose(): void {
    this.colorSpaceManager = null
  }

  // ==================== 内部方法 ====================

  private getShaderEffectHostByNodeId(nodeId: string):
    | {
        setShaderEffect(effect: GSplatShaderEffectLike | null): boolean
        clearShaderEffect(): boolean
        restartShaderEffect(): boolean
        getShaderEffect?(): GSplatShaderEffectLike | null
      }
    | undefined {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (lodEntry) return lodEntry.renderMesh

    const singleEntry = this.ctx.registry.getSingleByNodeId(nodeId)
    if (singleEntry) return singleEntry.mesh

    return undefined
  }

  /** 根据 nodeId 获取模型的材质 */
  private getMaterialByNodeId(nodeId: string): GaussianSplatMaterial | undefined {
    const lodEntry = this.ctx.registry.getLODByNodeId(nodeId)
    if (lodEntry) return lodEntry.renderMesh.material

    const singleEntry = this.ctx.registry.getSingleByNodeId(nodeId)
    if (singleEntry) return singleEntry.mesh.material

    return undefined
  }

  /** 确保颜色空间管理器已初始化 */
  private ensureColorSpaceManager(): ColorSpaceManager {
    if (!this.colorSpaceManager) {
      this.colorSpaceManager = new ColorSpaceManager(this.ctx.renderer)
    }
    return this.colorSpaceManager
  }

  /** 获取 LODGroupEntry 对应的 GSplatManager */
  private getGSplatManager(entry: LODGroupEntry): GSplatManager | undefined {
    const director = this.ctx.getDirector()
    const camera = this.ctx.getLastCamera()
    if (!director || !camera) return undefined
    return director.getManager(camera, entry.layer) ?? undefined
  }
}
